import React, { useEffect, useRef, useState } from 'react';
import { Download, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * Discrete "Install app" trigger.
 * - On Chrome/Edge/Android: uses the native `beforeinstallprompt` event.
 * - On iOS Safari: shows a clear popover with the Share icon and steps.
 * - Hidden if the app is already installed.
 */

// iOS Safari Share icon (SVG matches the system glyph: square with up-arrow)
function IOSShareIcon({ size = 22, className = '' }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M12 15V3" />
      <path d="m8 7 4-4 4 4" />
      <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
  );
}

// "Add" plus icon used in the iOS share sheet next to "Add to Home Screen"
function IOSAddIcon({ size = 18 }) {
  return (
    <span
      className="inline-flex items-center justify-center rounded border border-current"
      style={{ width: size, height: size }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size - 6}
        height={size - 6}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M12 5v14" />
        <path d="M5 12h14" />
      </svg>
    </span>
  );
}

export default function PWAInstallButton({ className = '' }) {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const ua = window.navigator.userAgent || '';
    const iOS = /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    setIsIOS(iOS);

    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) setInstalled(true);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Close popover on outside click / Esc
  useEffect(() => {
    if (!showHint) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setShowHint(false);
    };
    const onKey = (e) => e.key === 'Escape' && setShowHint(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showHint]);

  if (installed) {
    return (
      <div
        className={`inline-flex items-center gap-1.5 text-xs text-white/60 ${className}`}
        data-testid="pwa-installed-badge"
      >
        <Check size={14} />
        <span>{t('pwa.installed', 'App installée')}</span>
      </div>
    );
  }

  // Render nothing when there's nothing to offer (desktop Chrome before SW activates, etc.)
  if (!deferredPrompt && !isIOS) return null;

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch {}
      setDeferredPrompt(null);
    } else if (isIOS) {
      setShowHint((s) => !s);
    }
  };

  return (
    <div ref={wrapRef} className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        data-testid="pwa-install-button"
        className="inline-flex items-center gap-1.5 text-xs text-white/70 hover:text-white transition-colors underline-offset-4 hover:underline"
      >
        <Download size={14} />
        <span>{t('pwa.install', "Installer l'app")}</span>
      </button>

      {isIOS && showHint && (
        <div
          data-testid="pwa-ios-hint"
          role="dialog"
          className="absolute bottom-full right-0 mb-3 z-50 w-72 bg-white text-[#1A3C34] rounded-lg shadow-2xl border border-black/5 p-4 animate-in fade-in slide-in-from-bottom-2 duration-200"
        >
          <button
            type="button"
            onClick={() => setShowHint(false)}
            aria-label="Close"
            className="absolute top-2 right-2 text-[#1A3C34]/40 hover:text-[#1A3C34]"
          >
            <X size={16} />
          </button>
          <h4 className="font-heading text-sm font-bold mb-2 pr-4">
            {t('pwa.iosTitle', 'Installer Flexi sur iPhone')}
          </h4>
          <p className="text-[11px] text-[#C77D63] uppercase tracking-wider font-semibold mb-2">
            {t('pwa.iosSafariOnly', 'Safari uniquement')}
          </p>
          <ol className="space-y-2.5 text-xs leading-relaxed">
            <li className="flex items-start gap-2">
              <span className="font-bold text-[#1A3C34] flex-shrink-0">1.</span>
              <span className="flex-1">
                {t('pwa.iosStep1', 'Touchez le bouton')}{' '}
                <span className="inline-flex items-center gap-1 align-middle">
                  <strong>{t('pwa.iosShare', 'Partager')}</strong>
                  <IOSShareIcon size={18} className="text-[#1A3C34]" />
                </span>{' '}
                {t('pwa.iosStep1End', 'en bas de Safari')}
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-[#1A3C34] flex-shrink-0">2.</span>
              <span className="flex-1">
                {t('pwa.iosStep2', 'Faites défiler et touchez')}{' '}
                <span className="inline-flex items-center gap-1 align-middle">
                  <IOSAddIcon size={16} />
                  <strong>{t('pwa.iosAddHome', 'Sur l\'écran d\'accueil')}</strong>
                </span>
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="font-bold text-[#1A3C34] flex-shrink-0">3.</span>
              <span className="flex-1">
                {t('pwa.iosStep3', 'Touchez Ajouter en haut à droite')}
              </span>
            </li>
          </ol>
        </div>
      )}
    </div>
  );
}
